import React from 'react';
import { Container, Header, Segment, Card, Message, List, Button, Icon } from 'semantic-ui-react';
import { observer } from 'mobx-react';
import '../Playbook.css';

const OrganizationalStructures = observer(() => {
    const handleDownloadMatrix = () => {
        const link = document.createElement('a');
        link.href = '/downloads/sample-career-matrix.xlsx';
        link.download = 'sample-career-matrix.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Container className="playbook-container">
            <h1 className="ui icon center aligned header playbook-branded-header" style={{fontFamily: 'Montserrat', textTransform: 'uppercase', color: 'rgb(35, 37, 58)', fontWeight: 600, fontSize: '38px', marginBottom: '-9px'}}>
                <i aria-hidden="true" className="icon">
                    <img src="/static/media/logo-icon.7bb8994723bbdcd674f1.png" alt="logo-icon" style={{height: '68px', marginBottom: '-15px'}} />
                </i>
                Skills/Leadership
            </h1>
            <div className="ui divider"></div>

            <Segment className="playbook-section">
                <Header as="h2" className="playbook-section-header">
                    Sales Organizational Structures
                </Header>
                
                <p className="playbook-text">
                    When creating a sales organization, there are multiple ways to structure it. Some are 
                    very simple where you have a "salesperson" who does everything from lead gen to closing 
                    and who reports to the CEO.
                </p>

                <p className="playbook-text">
                    In other organizations, you have roles with layer(s) of management. In other words, 
                    this is not a "one size fits all" process. The key it to create an organizational 
                    structure that not only works but is also based on a career path. It's not just a 
                    pure organizational structure.
                </p>

                <p className="playbook-text">
                    In general, the progression for a salesperson is to begin with lead generation and 
                    then move to customer-based sales where the roles and responsibilities can change 
                    based on the company and the products, solutions, etc., that are to be sold.
                </p>

                <p className="playbook-text">
                    There also is the "account management" role that dovetails with sales. In general, 
                    account management requires more experience. However, that doesn't mean it is harder 
                    than an "outside" sales role. Account management just requires more experience in 
                    managing stakeholders.
                </p>

                <p className="playbook-text">
                    In short, you want to set your team members up to be successful in their career paths. 
                    To do that, you want to equip them with the skills they need to be successful.
                </p>

                <p className="playbook-text">
                    When you look at the spreadsheet linked below, you will see the skills required. 
                    This is our take on the skills necessary to be successful and to eventually go 
                    from a junior role to a senior role to a leadership role.
                </p>

                <div style={{textAlign: 'center', margin: '20px 0'}}>
                    <img 
                        src="/downloads/career-path.jpg" 
                        alt="Career Path Progression" 
                        style={{maxWidth: '100%', height: 'auto', border: '1px solid #ddd', borderRadius: '4px'}}
                    />
                </div>
            </Segment>

            <Segment className="playbook-section">
                <Header as="h2" className="playbook-section-header">
                    Words of Caution
                </Header>
                
                <p className="playbook-text">
                    If you think your best salesperson will automatically be the best manager because 
                    he or she sells the best, you are mistaken. That could be true, or it could be a 
                    PAINFUL mistake to make because it creates a multiplying expense in the following ways:
                </p>

                <List bulleted>
                    <List.Item>You lose your top producer as he or she is now managing and not out selling.</List.Item>
                    <List.Item>You don't have an effective manager leading your team, and the team is not producing at max output.</List.Item>
                </List>

                <p className="playbook-text">
                    Furthermore, the employment duration of a Chief Revenue Officer is 2 years on average. 
                    There are many reasons for this, but one of the driving factors of the short stints is 
                    that many times the CRO role is filled with a person who is great at sales but lacks 
                    the skills to manage.
                </p>

                <p className="playbook-text">
                    As a leader, whether you are hiring for a new role and/or growing a team from within, 
                    you want to create an organization that builds verifiable skills that will make your 
                    team successful. And as a salesperson, you want to be part of an organization that 
                    sets you up for success by defining the skills you need that accompany the activities 
                    you need to execute in order to maximize your sales capabilities.
                </p>

                <p className="playbook-text">
                    That's where the skills matrix comes in. The matrix gives a clear definition of roles 
                    and the expected activities and skills to create success in each role. And it also 
                    creates the opportunity for managers to interject objectivity combined with job 
                    performance results into the promotion process.
                </p>
            </Segment>

            <Segment className="playbook-section">
                <Header as="h2" className="playbook-section-header">
                    Sample Skills Matrix and Career Path
                </Header>
                
                <p className="playbook-text">
                    This won't be a perfect match but is a very good method for you to think though to 
                    build out your organizational structure and how you will set your team members up for success by:
                </p>

                <List bulleted>
                    <List.Item>Equipping them to be successful</List.Item>
                    <List.Item>Positioning them to be successful</List.Item>
                    <List.Item>Seeing them achieve personal gain that creates corporate growth</List.Item>
                </List>

                <Card fluid>
                    <Card.Content>
                        <Card.Header>
                            <Icon name="download" />
                            Sample Career Matrix
                        </Card.Header>
                        <Card.Description>
                            <p>Download our comprehensive skills matrix template to build your organizational structure and career development framework.</p>
                            <Button 
                                primary 
                                icon="download" 
                                labelPosition="left"
                                content="Download Career Matrix"
                                onClick={handleDownloadMatrix}
                            />
                        </Card.Description>
                    </Card.Content>
                </Card>
            </Segment>
        </Container>
    );
});

export default OrganizationalStructures;